import { useEffect, useState } from "react";
import axiosClient from "../api/api";
import { useTheme } from "./useTheme";
import { getContrastTextColor } from "../utils/themeUtils";

interface BrandingData {
  logo_url: string;
  primary_color: string;
  secondary_color: string;
}

export const useBranding = (id: string | undefined) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { updateBranding } = useTheme();

  useEffect(() => {
    if (!id) {
      console.log("useBranding: No id provided, skipping");
      return;
    }

    const fetchBranding = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axiosClient.get(`/retailerProduct/api/settings/catalogue/branding`, {
          params: {
            login_id: id,
          },
        });

        if (response.data && response.data.success) {
          const brandingData: BrandingData = {
            logo_url: response.data.data.logo_url || "",
            primary_color: response.data.data.primary_color || "#24588C",
            secondary_color: response.data.data.secondary_color || "#16A34A",
          };

          // Update CSS variables and ThemeContext
          document.documentElement.style.setProperty("--primary-color", brandingData.primary_color);
          document.documentElement.style.setProperty("--secondary-color", brandingData.secondary_color);
          document.documentElement.style.setProperty("--secondary-contrast-text", getContrastTextColor(brandingData.secondary_color));
          updateBranding(brandingData);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        setError("Failed to load branding");

        // Set default branding when API fails
        const defaultBranding: BrandingData = {
          logo_url: "",
          primary_color: "#24588C",
          secondary_color: "#16A34A",
        };
        document.documentElement.style.setProperty("--primary-color", defaultBranding.primary_color);
        document.documentElement.style.setProperty("--secondary-color", defaultBranding.secondary_color);
        document.documentElement.style.setProperty("--secondary-contrast-text", getContrastTextColor(defaultBranding.secondary_color));
        updateBranding(defaultBranding);
      } finally {
        setLoading(false);
      }
    };

    // Always call API on every mount/reload - no caching
    fetchBranding();
  }, [id]);

  return { loading, error };
};
